'use client'

import { useState, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ContactFormSchema, type ContactFormValues } from './schema'
import { sendContactForm } from './actions'
import Button from '@/components/Button'
import Input from '@/components/Form/Input'
import Textarea from '@/components/Form/Textarea'
import tick from '../../../public/icon-tick.svg'
import { type CommonProps } from '@/utils/types'
import classNames from '@/utils/classNames'
import Image from 'next/image'

const ContactForm: FC<ContactFormProps> = (props) => {
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string>('')
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(ContactFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      message: ''
    }
  })

  const onSubmit = async (formValues: ContactFormValues) => {
    const result = await sendContactForm(formValues)

    if (result.success) {
      setSuccess(true)
      form.reset()
    }
    else {
      setError(result.error ?? '')
    }
  }

  if (success) {
    return (
      <div className={classNames('text-center py-45 animate-in fade-in-0 duration-300', props.className)}>
        <Image
          src={tick}
          alt="The image of a tick"
          width={60}
          height={60}
          className="mx-auto"
        />
        <p className="font-raleway font-bold py-6 text-[31px]">Message sent successfully!</p>
        <p className="text-[22px]">We will get back to you as soon as possible</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={classNames('text-center py-45 animate-in fade-in-0 duration-300', props.className)}>
        <p className="font-raleway font-bold py-6 text-[31px]">Error sending message</p>
        <p className="text-[22px]">{error}</p>
      </div>
    )
  }

  return (
    <form {...props} onSubmit={form.handleSubmit(onSubmit)}>
      <p>Required fields are marked with an asterisk (*).</p>
      <div className="space-y-5 pt-8">
        <div className="flex gap-6">
          <div className="w-1/2">
            <Controller
              name="firstName"
              control={form.control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="firstName"
                  label="First Name *"
                  placeholder="First Name"
                  hasError={!!form.formState.errors.firstName}
                  errorMessage={form.formState.errors.firstName?.message}
                  variant="transparent"
                />
              )}
            />
          </div>
          <div className="w-1/2">
            <Controller
              name="lastName"
              control={form.control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="lastName"
                  label="Last Name *"
                  placeholder="Last Name"
                  hasError={!!form.formState.errors.lastName}
                  errorMessage={form.formState.errors.lastName?.message}
                  variant="transparent"
                />
              )}
            />
          </div>
        </div>
        <div>
          <Controller
            name="email"
            control={form.control}
            render={({ field }) => (
              <Input
                {...field}
                id="email"
                label="Email *"
                placeholder="Email"
                hasError={!!form.formState.errors.email}
                errorMessage={form.formState.errors.email?.message}
                variant="transparent"
              />
            )}
          />
        </div>
        <div>
          <Controller
            name="message"
            control={form.control}
            render={({ field }) => (
              <Textarea
                {...field}
                id="message"
                label="Message *"
                placeholder="Message"
                hasError={!!form.formState.errors.message}
                errorMessage={form.formState.errors.message?.message}
                variant="transparent"
                style={{ height: '120px' }}
              />
            )}
          />
        </div>
      </div>
      <p className="text-xs pt-6">
        I agree to the processing of my data and declare that I have read the
        {' '}
        <a className="underline" href="https://www.1ocean.org/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      </p>
      <div className="py-8 flex justify-center">
        <Button type="submit" variant="accent" aria-label="Send message" disabled={form.formState.isSubmitting}>
          <span className="px-9">{form.formState.isSubmitting ? 'Sending...' : 'Send message'}</span>
        </Button>
      </div>
    </form>
  )
}

export interface ContactFormProps extends CommonProps {
}

export default ContactForm
